import { useCallback } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { Icon } from "@dialogueconsulting/sked-ui";
import { FIELDS } from "constants/PostFormFields";
import { useTrackEvent } from "libs/analytics";
import { COVER_IMAGE_SELECTED } from "constants/Events";
import {
  VIDEO_COVER_TYPES,
  VIDEO_COVER_TIMESTAMPS,
} from "constants/VideoCoverConstants";
import { getVideoPreviewImage } from "../utils";

const ToastWrap = styled.div`
  display: flex;
  span {
    margin-left: 10px;
  }
`;

interface Platform {
  TYPE: string;
  NAME: string;
}

interface UseVideoCoverOperationsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateField: (field: string, value: any) => void;
  platforms: Platform[];
  originalMediaUrl?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
  videoCoverImageTimestamp?: number;
  videoCoverPreview?: string;
  setCover: (cover: string) => void;
  setIsLoadingThumb: (loading: boolean) => void;
  setOriginalValues: (values: {
    timestamp?: number;
    coverType?: string;
  }) => void;
  platformFullname: string;
}

export function useVideoCoverOperations({
  updateField,
  platforms,
  originalMediaUrl,
  videoUrl,
  thumbnailUrl,
  videoCoverImageTimestamp,
  videoCoverPreview,
  setCover,
  setIsLoadingThumb,
  setOriginalValues,
  platformFullname,
}: UseVideoCoverOperationsProps) {
  const trackEvent = useTrackEvent();

  const onCoverChanged = useCallback(
    async (
      coverThumbnailUrl: string,
      timestampInMs: number,
      message?: string
    ) => {
      let highQualityThumb = coverThumbnailUrl;
      if (originalMediaUrl && timestampInMs) {
        setIsLoadingThumb(true);
        highQualityThumb = await getVideoPreviewImage(
          originalMediaUrl,
          timestampInMs / 1000
        );
        setIsLoadingThumb(false);
      }

      setCover(highQualityThumb);
      updateField(FIELDS.VIDEO_COVER_PREVIEW, highQualityThumb);
      updateField(FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP, timestampInMs);
      updateField(FIELDS.VIDEO_COVER_TYPE, VIDEO_COVER_TYPES.FRAME_SELECTED);

      if (message) {
        toast.success(
          <ToastWrap>
            <Icon name="check-circle-broken" size={18} color="#fff" />
            <span>{message}</span>
          </ToastWrap>
        );
      }
      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Frame",
        },
        useServices: ["AMP"],
      });
    },
    [
      originalMediaUrl,
      setCover,
      setIsLoadingThumb,
      updateField,
      trackEvent,
      platforms,
    ]
  );

  const onCoverUploaded = useCallback(
    (url: string, thumbnailUrl: string) => {
      setCover(thumbnailUrl);

      updateField(FIELDS.VIDEO_COVER_PREVIEW, url);
      updateField(FIELDS.THUMBNAILURL, url);
      updateField(
        FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP,
        VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
      );
      updateField(FIELDS.VIDEO_COVER_TYPE, VIDEO_COVER_TYPES.UPLOADED);
      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Upload",
        },
        useServices: ["AMP"],
      });
    },
    [setCover, updateField, trackEvent, platforms]
  );

  const onRevertToOriginal = useCallback(
    (
      originalCover: string,
      originalTimestamp?: number,
      originalCoverType?: string
    ) => {
      const revertTimestamp = originalTimestamp ?? 0;
      const revertType =
        originalCoverType ?? VIDEO_COVER_TYPES.PLATFORM_DEFAULT;

      setCover(originalCover);
      updateField(FIELDS.VIDEO_COVER_PREVIEW, originalCover);
      updateField(FIELDS.VIDEO_COVER_IMAGE_TIMESTAMP, revertTimestamp);
      updateField(FIELDS.VIDEO_COVER_TYPE, revertType);

      toast.success(
        <ToastWrap>
          <Icon name="check-circle-broken" size={18} color="#fff" />
          <span>{`The ${platformFullname} video cover has been reverted to original.`}</span>
        </ToastWrap>
      );

      trackEvent({
        eventName: COVER_IMAGE_SELECTED,
        metadata: {
          platforms,
          selected: "Revert",
        },
        useServices: ["AMP"],
      });
    },
    [setCover, updateField, platformFullname, trackEvent, platforms]
  );

  const generateOriginalCover = useCallback(async () => {
    if (!videoUrl) return;

    setIsLoadingThumb(true);
    try {
      let thumb;

      if (videoCoverPreview) {
        thumb = videoCoverPreview;
      } else if (
        thumbnailUrl &&
        videoCoverImageTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
      ) {
        thumb = thumbnailUrl;
      } else if (thumbnailUrl) {
        thumb = thumbnailUrl;
      } else {
        thumb = await getVideoPreviewImage(
          videoUrl,
          (videoCoverImageTimestamp || 0) / 1000
        );
      }

      setCover(thumb);
      updateField(FIELDS.VIDEO_COVER_ORIGINAL, thumb);
      updateField(FIELDS.VIDEO_COVER_PREVIEW, thumb);

      // Set appropriate cover type based on source
      let coverType;
      if (videoCoverPreview) {
        coverType = VIDEO_COVER_TYPES.FRAME_SELECTED;
      } else if (
        thumbnailUrl &&
        videoCoverImageTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER
      ) {
        coverType = VIDEO_COVER_TYPES.UPLOADED;
      } else if (thumbnailUrl) {
        coverType = VIDEO_COVER_TYPES.PLATFORM_DEFAULT;
      } else {
        coverType = VIDEO_COVER_TYPES.FRAME_SELECTED;
      }

      updateField(FIELDS.VIDEO_COVER_TYPE, coverType);

      // Store original values for revert functionality
      if (videoCoverImageTimestamp !== undefined) {
        setOriginalValues({ timestamp: videoCoverImageTimestamp, coverType });
      }
    } catch {
      toast.error(
        <ToastWrap>
          <Icon name="x-circle" size={18} color="#FA4F38" />
          <span>{`There was an issue selecting your cover, please try again or contact Support.`}</span>
        </ToastWrap>
      );
    } finally {
      setIsLoadingThumb(false);
    }
  }, [
    videoUrl,
    videoCoverPreview,
    thumbnailUrl,
    videoCoverImageTimestamp,
    setCover,
    setIsLoadingThumb,
    setOriginalValues,
    updateField,
  ]);

  return {
    onCoverChanged,
    onCoverUploaded,
    onRevertToOriginal,
    generateOriginalCover,
  };
}
